import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState(''); // Changed from username to email
  const [password, setPassword] = useState('');
  // Use isLoading from context to avoid duplicate state and reflect auth check
  const { login, isAuthenticated, isLoading: authLoading } = useAdminAuth();
  const [isSubmitting, setIsSubmitting] = useState(false); // Local submitting state for button
  const navigate = useNavigate();

  useEffect(() => {
    console.log('AdminLogin: Mounted. Path:', window.location.pathname);
    console.log('AdminLogin: Initial Auth State - isAuthenticated:', isAuthenticated, 'isLoading:', authLoading);

    // Redirect if already authenticated and auth is not loading
    if (isAuthenticated && !authLoading) {
      console.log('AdminLogin: Already authenticated, redirecting to /admin');
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Removed useEffect checking local_admins as it's no longer used for login

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('AdminLogin: Form submitted.');

    if (!email.trim() || !password.trim()) {
      toast.error('Preencha o email e a senha.');
      return;
    }

    setIsSubmitting(true); // Start local submitting indicator
    console.log(`AdminLogin: Attempting login for email: ${email}`);

    try {
      const success = await login(email, password); // Pass email to login function

      if (success) {
        console.log('AdminLogin: Login successful via context.');
        toast.success('Login realizado com sucesso!');
        // Navigate immediately after successful login
        navigate('/admin', { replace: true });
      } else {
        console.log('AdminLogin: Login failed according to context.');
        toast.error('Email ou senha inválidos. Tente novamente.');
      }
    } catch (error) {
      console.error('AdminLogin: Exception during login call:', error);
      toast.error('Erro ao processar login. Verifique o console.');
    } finally {
      setIsSubmitting(false); // Stop local submitting indicator
      console.log('AdminLogin: Finished login attempt.');
    }
  };

  // Render loading state or login form
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div>Carregando autenticação...</div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-navy-700">ALFA PEIXES</h1>
          <p className="text-sm text-gray-500 mt-2">Área Administrativa</p>
        </div>

        <Alert className="bg-blue-50 border-blue-200 text-blue-800">
          <InfoIcon className="h-4 w-4 mr-2" />
          <AlertDescription>
             Faça login com o email e senha do administrador.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Digite seu email"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="password">
              Senha
            </label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Digite sua senha"
              required
              autoComplete="current-password"
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || authLoading}
          >
            {isSubmitting ? 'Entrando...' : 'Entrar'}
          </Button>

          <div className="flex justify-between items-center mt-4 text-sm">
            <Link to="/admin/forgot-password" className="text-blue-600 hover:underline">
              Esqueci minha senha
            </Link>
            <Link to="/" className="text-gray-500 hover:underline">
              Voltar para a loja
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AdminLogin;
